import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { developer } from '@repletoryreach/sdk-api';
import { Stack, Group, Card, Text, Badge, Button, Anchor } from '@repletoryreach/sdk-ui';
import { PageHeader, Tabs, Alert, EmptyState } from '@repletoryreach/sdk-ui/data-display';
import { TextInput, Select } from '@repletoryreach/sdk-ui/inputs';
import { Menu } from '@repletoryreach/sdk-ui/overlays';
import { notifyError } from '@repletoryreach/sdk-ui/notifications';
import { downloadText } from '@repletoryreach/sdk-utils/file';
import { useT } from '../../i18n/I18nProvider';
import { publicApiUrl } from '../../lib/public-api';
import { formatDate } from '../../lib/format';
import { readEndpoints, readGroups } from './spec';
import { KeyField } from './components/KeyField';
import { OperationCard } from './components/OperationCard';
import { GettingStarted } from './components/GettingStarted';
import { WebhookEvents } from './components/WebhookEvents';
import { Changelog } from './components/Changelog';
import './developers.css';
import { HelpButton } from '../../app/guides/HelpButton';

/**
 * The developer documentation for the API a customer's own key can call.
 *
 * <b>Everything on this page is derived.</b> The endpoint list, the parameters,
 * the schemas, the error codes and the curl commands all come from the document
 * the gateway assembles at `/v1/developer/openapi.json`, which is itself built
 * from the services' own OpenAPI documents and filtered to the operations an
 * API key may reach. Nothing about an endpoint is typed into this page, so
 * nothing on it can drift from the API it describes.
 *
 * The one thing this page holds that the API does not is the developer's key,
 * and it holds it in React state for as long as the tab is open and nowhere
 * else. See {@link KeyField}.
 */
export default function DevelopersPage() {
  const t = useT();
  const baseUrl = publicApiUrl();

  // Memory only. Deliberately not useLocalStorage, not sessionStorage, not the
  // app store: a key that survives a refresh is a key sitting on disk.
  const [apiKey, setApiKey] = useState('');

  const [tab, setTab] = useState('start');
  const [search, setSearch] = useState('');
  const [group, setGroup] = useState('all');
  const [scope, setScope] = useState('all');

  const specQuery = useQuery({
    queryKey: ['developer', 'openapi'],
    queryFn: () => developer.getOpenApi(),
    staleTime: 5 * 60_000
  });

  const versionsQuery = useQuery({
    queryKey: ['developer', 'versions'],
    queryFn: () => developer.listVersions(),
    staleTime: 5 * 60_000
  });

  const changelogQuery = useQuery({
    queryKey: ['developer', 'changelog'],
    queryFn: () => developer.getChangelog(),
    staleTime: 5 * 60_000
  });

  const doc = specQuery.data;
  const endpoints = useMemo(() => readEndpoints(doc), [doc]);
  const groups = useMemo(() => readGroups(endpoints), [endpoints]);
  const scopes = doc?.['x-rr-scopes'] ?? [];
  const versions = versionsQuery.data?.data ?? [];

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return endpoints.filter((endpoint) => {
      if (group !== 'all' && endpoint.group !== group) return false;
      if (scope !== 'all' && endpoint.scope !== scope) return false;
      if (!needle) return true;
      return (
        endpoint.path.toLowerCase().includes(needle) ||
        endpoint.summary.toLowerCase().includes(needle) ||
        endpoint.method.toLowerCase().includes(needle) ||
        endpoint.scope.toLowerCase().includes(needle)
      );
    });
  }, [endpoints, search, group, scope]);

  async function download(version: string | null) {
    try {
      const body = version ? await developer.getVersion(version) : await developer.getOpenApi();
      const name = version
        ? `repletoryreach-public-api-${version}.json`
        : `repletoryreach-public-api-${doc?.info.version ?? 'current'}.json`;
      downloadText(JSON.stringify(body, null, 2), name, 'application/json');
    } catch (error) {
      notifyError(
        error instanceof Error ? error.message : t('developers.download.failed'),
        t('developers.title')
      );
    }
  }

  const version = doc?.info.version;
  const released = doc?.info['x-rr-released-on'];

  return (
    <Stack gap="lg">
      <PageHeader
        title={t('developers.title')}
        description={t('developers.description')}
        withDivider={false}
        actions={
          <Group gap="sm" wrap="wrap" align="center">
            <HelpButton guide="account" />
            {version && (
              // The version is an identifier and the date is a date: neither
              // reads well shouted, which is what a Badge does by default.
              <Badge variant="light" className="rr-dev__identifier">
                {t('developers.versionBadge', { version, date: formatDate(released) })}
              </Badge>
            )}
            <Menu>
              <Menu.Target>
                <Button size="sm" variant="outline">
                  {t('developers.download.button')}
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item onClick={() => download(null)}>
                  {t('developers.download.current')}
                </Menu.Item>
                {versions
                  .filter((v) => !v.current)
                  .map((v) => (
                    <Menu.Item key={v.version} onClick={() => download(v.version)}>
                      {t('developers.download.version', { version: v.version })}
                    </Menu.Item>
                  ))}
              </Menu.Dropdown>
            </Menu>
          </Group>
        }
      />

      {specQuery.error && (
        <Alert color="error" role="alert" title={t('developers.loadError.title')}>
          <Stack gap="sm">
            <Text role="body">{t('developers.loadError.body')}</Text>
            <Group gap="sm">
              <Button size="sm" onClick={() => specQuery.refetch()}>
                {t('action.retry')}
              </Button>
            </Group>
          </Stack>
        </Alert>
      )}

      <div data-guide="dev-key"><KeyField apiKey={apiKey} onChange={setApiKey} /></div>

      <div data-guide="dev-tabs">
      <Tabs value={tab} onChange={setTab}>
        <Tabs.List>
          <Tabs.Tab value="start">{t('developers.tab.start')}</Tabs.Tab>
          <Tabs.Tab value="reference">{t('developers.tab.reference')}</Tabs.Tab>
          <Tabs.Tab value="webhooks">{t('developers.tab.webhooks')}</Tabs.Tab>
          <Tabs.Tab value="changelog">{t('developers.tab.changelog')}</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="start">
          <GettingStarted doc={doc} baseUrl={baseUrl} apiKey={apiKey} />
        </Tabs.Panel>

        <Tabs.Panel value="reference">
          <Stack gap="md">
            <Card withBorder>
              <Group gap="sm" wrap="wrap" align="flex-end">
                <TextInput
                  label={t('developers.filter.search')}
                  value={search}
                  onChange={(e) => setSearch(e.currentTarget.value)}
                  placeholder={t('developers.filter.searchPlaceholder')}
                  className="rr-dev__search"
                  description={t('developers.filter.searchHint')}
                />
                <Select
                  label={t('developers.filter.group')}
                  value={group}
                  onChange={(e) => setGroup(e.currentTarget.value)}
                  data={[
                    { value: 'all', label: t('developers.filter.allGroups') },
                    ...groups.map((g) => ({ value: g, label: g }))
                  ]}
                />
                <Select
                  label={t('developers.filter.scope')}
                  value={scope}
                  onChange={(e) => setScope(e.currentTarget.value)}
                  data={[
                    { value: 'all', label: t('developers.filter.allScopes') },
                    ...scopes.map((s) => ({ value: s.name, label: s.name }))
                  ]}
                />
              </Group>
            </Card>

            {specQuery.isPending ? (
              <Text role="caption" c="var(--rr-color-text-muted)">
                {t('common.loading')}
              </Text>
            ) : visible.length === 0 ? (
              <EmptyState
                title={t('developers.empty.title')}
                description={
                  endpoints.length === 0
                    ? t('developers.empty.noSpec')
                    : t('developers.empty.noMatch')
                }
                action={
                  endpoints.length > 0 ? (
                    <Button
                      size="sm"
                      variant="subtle"
                      onClick={() => {
                        setSearch('');
                        setGroup('all');
                        setScope('all');
                      }}
                    >
                      {t('developers.empty.clear')}
                    </Button>
                  ) : undefined
                }
              />
            ) : (
              <Stack gap="lg">
                {groups
                  .filter((g) => visible.some((endpoint) => endpoint.group === g))
                  .map((g) => (
                    <Stack key={g} gap="sm">
                      <Text role="subtitle">{g}</Text>
                      {visible
                        .filter((endpoint) => endpoint.group === g)
                        .map((endpoint) => (
                          <OperationCard
                            key={endpoint.id}
                            endpoint={endpoint}
                            doc={doc}
                            baseUrl={baseUrl}
                            apiKey={apiKey}
                          />
                        ))}
                    </Stack>
                  ))}
              </Stack>
            )}

            <Card withBorder>
              <Stack gap="sm">
                <Text role="subtitle">{t('developers.notHere.title')}</Text>
                <Text role="body">{t('developers.notHere.body')}</Text>
                <Anchor href={`mailto:support@repletoryreach.co.za`}>
                  {t('developers.notHere.contact')}
                </Anchor>
              </Stack>
            </Card>
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="webhooks">
          <WebhookEvents doc={doc} />
        </Tabs.Panel>

        <Tabs.Panel value="changelog">
          <Changelog
            releases={changelogQuery.data?.data ?? []}
            versions={versions}
            loading={changelogQuery.isPending}
          />
        </Tabs.Panel>
      </Tabs>
      </div>
    </Stack>
  );
}
